/**
 * HelloRetail Full Search Page
 *
 * Powers the /search?q=... page when "Use Hello Retail predictive search" is
 * enabled in Theme Settings.
 *
 * Depends on: hello-retail-helpers.js, hello-retail-api.js (both loaded globally via theme.liquid).
 * Config: window.hrFullSearchConfig (set inline by main-search.liquid)
 */

(function () {
  'use strict';

  var config            = window.hrFullSearchConfig || {};
  var PRODUCTS_PER_PAGE = config.productsPerPage || 24;

  // ─── State ────────────────────────────────────────────────────────────────
  var api;
  var currentQuery   = '';
  var currentPage    = 1;
  var currentSortBy  = '';
  var currentFilters = []; // active HR query strings e.g. ["extraData.colorName:Navy"]
  var colorHexMap    = {}; // persistent name→hex cache, e.g. { "Navy": "#0e213f" }
  var totalResults   = 0;

  // ─── DOM refs ─────────────────────────────────────────────────────────────
  var countEl              = null;
  var productListEl        = null;
  var noResultsEl          = null;
  var paginationEl         = null;
  var sortPopoverEl        = null;
  var filterDrawerEl       = null;
  var filterCountEl        = null;
  var filterApplyBtnEl     = null;
  var filterProductTypeEl  = null;
  var filterMaterialEl     = null;
  var filterSizesEl        = null;
  var filterColorEl        = null;
  var sortUiBound          = false;

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    if (!window.HelloRetailAPI || !window.HelloRetailHelpers) {
      console.warn('[HR Full Search] Dependencies not ready.');
      return;
    }

    api = new HelloRetailAPI();
    if (config.key) api.searchKey = config.key;

    countEl             = document.getElementById('hr-fs-count');
    productListEl       = document.getElementById('hr-fs-product-list');
    noResultsEl         = document.getElementById('hr-fs-no-results');
    paginationEl        = document.getElementById('hr-fs-pagination');
    sortPopoverEl       = document.getElementById('hr-sort-by-popover');
    filterDrawerEl      = document.getElementById('facets-drawer');
    filterCountEl       = document.getElementById('hr-filter-count');
    filterApplyBtnEl    = document.getElementById('hr-filter-apply-btn');
    filterProductTypeEl = document.getElementById('hr-filter-options-product-type');
    filterMaterialEl    = document.getElementById('hr-filter-options-material');
    filterSizesEl       = document.getElementById('hr-filter-options-sizes');
    filterColorEl       = document.getElementById('hr-filter-options-color');

    var urlParams  = new URLSearchParams(window.location.search);
    currentQuery   = urlParams.get('q') || '';
    currentPage    = parseInt(urlParams.get('page') || '1', 10) || 1;
    currentSortBy  = urlParams.get('sort_by') || '';
    currentFilters = urlParams.getAll('filter');

    _wireSortUI();
    _wireFilterApplyBtn();

    // Handle browser back/forward
    window.addEventListener('popstate', function () {
      var p          = new URLSearchParams(window.location.search);
      currentQuery   = p.get('q') || '';
      currentPage    = parseInt(p.get('page') || '1', 10) || 1;
      currentSortBy  = p.get('sort_by') || '';
      currentFilters = p.getAll('filter');
      _fetch(currentQuery, currentPage);
    });

    if (currentQuery) {
      _showPlaceholders();
      // Delay initial fetch until window.load so Hello Retail's boxes system
      // (ppBusiness.js, loaded via XHR by helloretail.js) has finished
      // initialising and won't overwrite our rendered content afterwards.
      if (document.readyState === 'complete') {
        _fetch(currentQuery, currentPage);
      } else {
        window.addEventListener('load', function () {
          _fetch(currentQuery, currentPage);
        }, { once: true });
      }
    }
  }

  // ─── Placeholders (loading) ─────────────────────────────────────────────

  function _showCountPlaceholder() {
    if (!countEl) return;
    if (countEl.querySelector('.hr-fs-skeleton-count') && !countEl.textContent.trim()) {
      countEl.setAttribute('aria-busy', 'true');
      return;
    }
    var countTpl = document.getElementById('hr-fs-count-placeholder-tpl');
    if (!countTpl || !countTpl.content) {
      countEl.innerHTML = '<span class="hr-fs-skeleton-count" aria-hidden="true"></span>';
    } else {
      countEl.innerHTML = '';
      countEl.appendChild(document.importNode(countTpl.content, true));
    }
    countEl.setAttribute('aria-busy', 'true');
  }

  function _showPlaceholders() {
    if (!productListEl) return;
    var hasProducts = productListEl.querySelector('.hr-product-card');
    var hasSkeletons = productListEl.querySelector('.hr-fs-skeleton-card');
    if (hasSkeletons && !hasProducts) {
      productListEl.setAttribute('aria-busy', 'true');
      return;
    }
    var tpl = document.getElementById('hr-fs-placeholders-tpl');
    if (!tpl || !tpl.content) return;
    productListEl.innerHTML = '';
    productListEl.appendChild(document.importNode(tpl.content, true));
    productListEl.setAttribute('aria-busy', 'true');
  }

  function _clearLoadingState() {
    if (productListEl) productListEl.removeAttribute('aria-busy');
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────

  function _fetch(query, page) {
    _showCountPlaceholder();
    _showPlaceholders();

    var start   = (page - 1) * PRODUCTS_PER_PAGE;
    var options = {
      return_filters: true,
      engine_options: {
        product: {
          current_count: PRODUCTS_PER_PAGE,
          start: start
        }
      }
    };

    var hrSort = _mapShopifySortToHr(currentSortBy);
    if (hrSort) options.sorting = [hrSort];

    if (currentFilters.length) options.filters = currentFilters;

    api.search(query, options)
      .then(function (data) {
        _render(data, query, page);
      })
      .catch(function (err) {
        console.error('[HR Full Search] Error:', err.message);
        if (productListEl) {
          productListEl.innerHTML = '';
        }
        if (countEl) {
          countEl.textContent = '';
          countEl.removeAttribute('aria-busy');
        }
        _clearLoadingState();
      });
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  function _render(data, query, page) {
    var H        = window.HelloRetailHelpers;
    var products = (data && data.result) ? data.result : [];
    totalResults  = (data && data.product_results) || 0;

    // Debug: inspect HR filter options from the API response (DevTools console)
    console.log('[HR Full Search] response', data);
    console.log('[HR Full Search] filters (filter options)', data.filters || {});

    // Populate filter drawer with available options
    _renderFilters(data.filters || {});

    // Update results count
    if (countEl) {
      countEl.removeAttribute('aria-busy');
      if (totalResults > 0) {
        var tpl = (config.labels && config.labels.resultsFor) || '__count__ results for "__terms__"';
        countEl.textContent = tpl
          .replace('__count__', totalResults)
          .replace('__terms__', query);
      } else {
        countEl.textContent = '';
      }
    }

    // No-results state
    if (noResultsEl) noResultsEl.hidden = products.length > 0;

    // Render product cards
    if (productListEl) {
      productListEl.innerHTML = '';
      if (products.length > 0) {
        var fragment = document.createDocumentFragment();
        products.forEach(function (product) {
          var card = H.cloneTemplate('hr-product-card-tpl');
          if (!card) return;
          H.fillSlots(card, H.CARD_SLOT_FILLERS, product);
          fragment.appendChild(card);
        });
        productListEl.appendChild(fragment);
      }
    }
    _clearLoadingState();

    // Pagination
    _renderPagination(totalResults, page);
  }

  // ─── Filters ──────────────────────────────────────────────────────────────

  function _renderFilters(filters) {
    // Kategori — extraData.productType
    if (filterProductTypeEl) {
      var productTypes = filters['extraData.productType'] || [];
      filterProductTypeEl.innerHTML = productTypes.map(function (item, i) {
        var label   = item.title || '';
        var inputId = 'hr-filter-product-type-' + i;
        var checked  = currentFilters.indexOf(item.query) !== -1 ? 'checked' : '';
        return (
          '<div class="checkbox-control">' +
            '<input id="' + inputId + '" class="dot-checkbox" type="checkbox" value="' + _esc(item.query) + '" ' + checked + '>' +
            '<label for="' + inputId + '">' + _esc(label) + '</label>' +
          '</div>'
        );
      }).join('');
    }

    // Materiale — extraDataList.material (fallback: extraData.material)
    if (filterMaterialEl) {
      var materials = filters['extraDataList.material'] || filters['extraData.material'] || [];
      filterMaterialEl.innerHTML = materials.map(function (item, i) {
        var inputId = 'hr-filter-material-' + i;
        var checked = currentFilters.indexOf(item.query) !== -1 ? 'checked' : '';
        return (
          '<div class="checkbox-control">' +
            '<input id="' + inputId + '" class="dot-checkbox" type="checkbox" value="' + _esc(item.query) + '" ' + checked + '>' +
            '<label for="' + inputId + '">' + _esc(item.title || '') + '</label>' +
          '</div>'
        );
      }).join('');
    }

    // Størrelse — sizes
    if (filterSizesEl) {
      var sizes = filters['extraDataList.sizes'] || [];
      filterSizesEl.innerHTML = sizes.map(function (item, i) {
        var inputId = 'hr-filter-size-' + i;
        var checked = currentFilters.indexOf(item.query) !== -1 ? 'checked' : '';
        return (
          '<div class="checkbox-control">' +
            '<input id="' + inputId + '" class="dot-checkbox" type="checkbox" value="' + _esc(item.query) + '" ' + checked + '>' +
            '<label for="' + inputId + '">' + _esc(item.title || '') + '</label>' +
          '</div>'
        );
      }).join('');
    }

    // Farve — color swatches
    if (filterColorEl) {
      var colorNames = filters['extraData.colorName'] || [];
      var colorHexes = filters['extraData.color']     || [];

      // Merge newly returned hex entries into the persistent map.
      // HR only returns extraData.color entries matching the active filter,
      // so we accumulate across renders to keep all hex values available.
      colorHexes.forEach(function (item) {
        if (!item.title) return;
        var parts = item.title.split(' | ');
        var name  = parts[0].trim();
        var hex   = parts[1] ? parts[1].trim() : '';
        if (name && hex) colorHexMap[name] = hex;
      });

      filterColorEl.innerHTML = colorNames.map(function (item, i) {
        var inputId  = 'hr-filter-color-' + i;
        var checked  = currentFilters.indexOf(item.query) !== -1 ? 'checked' : '';
        var hex      = colorHexMap[item.title] || '';
        var swatchBg = hex ? 'linear-gradient(to right, ' + hex + ', ' + hex + ')' : '';
        var style    = swatchBg ? ' style="--swatch-background: ' + swatchBg + '"' : '';
        return (
          '<input class="sr-only" id="' + inputId + '" type="checkbox" value="' + _esc(item.query) + '" ' + checked + '>' +
          '<label class="color-swatch" for="' + inputId + '"' + style + '>' +
            '<span class="color-swatch-title">' + _esc(item.title || '') + '</span>' +
          '</label>'
        );
      }).join('');
    }

    // Update active count on toolbar button
    if (filterCountEl) {
      filterCountEl.textContent = currentFilters.length ? ' (' + currentFilters.length + ')' : '';
    }
  }

  function _wireFilterApplyBtn() {
    if (!filterApplyBtnEl || !filterDrawerEl) return;

    filterApplyBtnEl.addEventListener('click', function () {
      // Collect all checked inputs inside the drawer
      var checked = filterDrawerEl.querySelectorAll('input:checked');
      currentFilters = [];
      for (var i = 0; i < checked.length; i++) {
        currentFilters.push(checked[i].value);
      }

      // Update count badge immediately
      if (filterCountEl) {
        filterCountEl.textContent = currentFilters.length ? ' (' + currentFilters.length + ')' : '';
      }

      // Sync filters to URL
      var uiUrl = new URL(window.location.href);
      uiUrl.searchParams.delete('filter');
      uiUrl.searchParams.delete('page');
      currentFilters.forEach(function (f) { uiUrl.searchParams.append('filter', f); });
      window.history.pushState({}, '', uiUrl.toString());

      // Close drawer — facets-drawer extends the theme's Drawer custom element
      if (typeof filterDrawerEl.hide === 'function') {
        filterDrawerEl.hide();
      }

      currentPage = 1;
      _fetch(currentQuery, 1);
    });
  }

  // ─── Pagination ───────────────────────────────────────────────────────────

  function _renderPagination(total, page) {
    if (!paginationEl) return;

    var totalPages = Math.ceil(total / PRODUCTS_PER_PAGE);

    if (totalPages <= 1) {
      paginationEl.innerHTML = '';
      return;
    }

    var items = '';

    if (page > 1) {
      items += '<button type="button" class="pagination__link h6" data-page="' + (page - 1) + '" aria-label="Previous page">&lsaquo;</button>';
    }

    var startPage = Math.max(1, page - 2);
    var endPage   = Math.min(totalPages, page + 2);

    if (startPage > 1) {
      items += '<button type="button" class="pagination__link h6" data-page="1">1</button>';
    }
    if (startPage > 2) {
      items += '<span class="pagination__link pagination__link--disabled pagination__dots h6">⋯</span>';
    }

    for (var i = startPage; i <= endPage; i++) {
      if (i === page) {
        items += '<span class="pagination__link pagination__link--disabled h6" aria-current="page">' + i + '</span>';
      } else {
        items += '<button type="button" class="pagination__link h6" data-page="' + i + '">' + i + '</button>';
      }
    }

    if (endPage < totalPages - 1) {
      items += '<span class="pagination__link pagination__link--disabled pagination__dots h6">⋯</span>';
    }
    if (endPage < totalPages) {
      items += '<button type="button" class="pagination__link h6" data-page="' + totalPages + '">' + totalPages + '</button>';
    }

    if (page < totalPages) {
      items += '<button type="button" class="pagination__link h6" data-page="' + (page + 1) + '" aria-label="Next page">&rsaquo;</button>';
    }

    paginationEl.innerHTML = '<nav class="pagination" role="navigation" aria-label="Pagination">' + items + '</nav>';

    paginationEl.querySelectorAll('[data-page]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var newPage = parseInt(btn.dataset.page, 10);
        currentPage = newPage;
        var url = new URL(window.location.href);
        url.searchParams.set('page', newPage);
        window.history.pushState({}, '', url.toString());
        _fetch(currentQuery, newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  // ─── Sort UI ──────────────────────────────────────────────────────────────

  function _wireSortUI() {
    if (sortUiBound || !sortPopoverEl) return;

    // Intercept native facet:update emitted by facets-sort-popover so we can
    // keep HR client-side rendering and skip Shopify's section reload flow.
    document.addEventListener('facet:update', function (event) {
      if (event.target !== sortPopoverEl) return;
      if (!event.detail || !event.detail.url) return;

      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      var url          = new URL(event.detail.url.toString(), window.location.origin);
      var selectedSort = url.searchParams.get('sort_by') || '';

      currentSortBy = selectedSort;
      currentPage   = 1;

      var uiUrl = new URL(window.location.href);
      if (currentSortBy) {
        uiUrl.searchParams.set('sort_by', currentSortBy);
      } else {
        uiUrl.searchParams.delete('sort_by');
      }
      uiUrl.searchParams.delete('page');
      // Re-write filter params to preserve them across sort changes
      uiUrl.searchParams.delete('filter');
      currentFilters.forEach(function (f) { uiUrl.searchParams.append('filter', f); });
      window.history.pushState({}, '', uiUrl.toString());

      _fetch(currentQuery, 1);
    }, true);

    sortUiBound = true;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function _mapShopifySortToHr(sortBy) {
    var map = {
      'price-ascending':    'price asc',
      'price-descending':   'price desc',
      'created-ascending':  'created asc',
      'created-descending': 'created desc'
    };
    return map[sortBy] || null;
  }

  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────

  function _boot() {
    if (window.HelloRetailHelpers) {
      init();
    } else {
      document.addEventListener('hr:helpers:ready', init);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _boot);
  } else {
    _boot();
  }

})();
